<script lang="ts">
  // Running timer, always visible in the sidebar (and as a pill on mobile).
  import { Pause, Play, Square, Save, Coffee } from '@lucide/svelte';
  import { timer } from '../stores/timer.svelte';
  import { data } from '../stores/data.svelte';
  import { formatClock, clientColor } from '../format';
  import { prefs } from '../prefs.svelte';
  import { t } from '../i18n.svelte';
  import { toast } from '../toast.svelte';
  import { errorMessage } from '../api';
  import { navigate } from '../router.svelte';
  import { tooltip } from '../ui/actions';
  import ColorDot from '../ui/ColorDot.svelte';

  let { variant = 'card', collapsed = false }: { variant?: 'card' | 'pill'; collapsed?: boolean } = $props();

  const s = $derived(timer.state);
  const client = $derived(s?.clientId ? data.clientById.get(Number(s.clientId)) : null);
  const color = $derived(client ? clientColor(client) : 'var(--brand)');
  const label = $derived(s?.clientName || client?.name || t('common.no_client'));
  const display = $derived(timer.onBreak ? formatClock(timer.currentBreakMs, prefs.value.showSeconds) : formatClock(timer.workMs, prefs.value.showSeconds));

  async function act(fn: () => Promise<void>, ok?: string) {
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }
</script>

{#if timer.active && s}
  {#if variant === 'pill'}
    <a class="pill" class:paused={timer.onBreak} class:stopped={timer.stopped} href="/app/timer" style:--c={color}>
      {#if timer.onBreak}<Coffee size={14} />{:else if timer.running}<span class="dot-live"></span>{:else}<Square size={12} />{/if}
      <span class="clock tabular">{display}</span>
    </a>
  {:else if collapsed}
    <a class="mini-collapsed" class:paused={timer.onBreak} href="/app/timer" use:tooltip={label} style:--c={color}>
      {#if timer.onBreak}<Coffee size={16} />{:else}<span class="dot-live"></span>{/if}
      <span class="clock tabular small">{formatClock(timer.workMs, false)}</span>
    </a>
  {:else}
    <div class="card" class:paused={timer.onBreak} class:stopped={timer.stopped} style:--c={color}>
      <button type="button" class="info" onclick={() => navigate('/app/timer')}>
        <span class="status eyebrow">
          {#if timer.onBreak}<span class="dot-paused"></span>{t('timer.status_break')}{:else if timer.running}<span class="dot-live"></span>{t('timer.status_running')}{:else}{t('timer.status_stopped')}{/if}
        </span>
        <span class="clock tabular">{display}</span>
        <span class="who"><ColorDot {color} size="xs" /><span class="truncate">{label}{#if s.activityName}<span class="act"> · {s.activityName}</span>{/if}</span></span>
      </button>
      <div class="actions">
        {#if timer.running}
          <button type="button" class="b" use:tooltip={timer.onBreak ? t('timer.resume') : t('timer.pause')} onclick={() => act(() => timer.togglePause())}>
            {#if timer.onBreak}<Play size={15} />{:else}<Pause size={15} />{/if}
          </button>
          <button type="button" class="b stop" use:tooltip={t('timer.stop')} onclick={() => act(() => timer.stop())}><Square size={13} /></button>
        {:else}
          <button type="button" class="b" use:tooltip={t('timer.continue')} onclick={() => act(() => timer.resume())}><Play size={15} /></button>
          <button type="button" class="b save" use:tooltip={t('timer.save')} onclick={() => act(() => timer.save(), t('timer.saved'))}><Save size={15} /></button>
        {/if}
      </div>
    </div>
  {/if}
{/if}

<style>
  .card {
    position: relative;
    display: flex; align-items: center; gap: var(--space-2);
    padding: var(--space-2_5) var(--space-2_5) var(--space-2_5) var(--space-3);
    border-radius: var(--radius);
    background: var(--surface);
    border: 1px solid var(--border);
    overflow: hidden;
  }
  .card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--c); }
  .card.paused::before { background: var(--pause); }
  .info { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 1px; background: none; border: 0; padding: 0; text-align: left; color: var(--text); }
  .status { display: inline-flex; align-items: center; gap: var(--space-1_5); font-size: var(--text-2xs); }
  .clock { font-size: var(--text-xl); font-weight: var(--weight-bold); letter-spacing: var(--tracking-snug); }
  .clock.small { font-size: var(--text-2xs); }
  .who { display: flex; align-items: center; gap: var(--space-1_5); font-size: var(--text-xs); color: var(--text-2); max-width: 100%; min-width: 0; }
  .act { color: var(--text-3); }
  .actions { display: flex; flex-direction: column; gap: var(--space-1); }
  .b { width: 30px; height: 30px; border-radius: var(--radius-xs); border: 0; background: var(--surface-3); color: var(--text); display: grid; place-items: center; }
  .b:hover { background: var(--surface-hover); }
  .b:focus-visible { outline: none; box-shadow: var(--ring); }
  .b.stop { color: var(--danger); }
  .b.save { color: var(--success); }

  .pill {
    display: inline-flex; align-items: center; gap: var(--space-1_5);
    height: var(--control-h-sm); padding: 0 var(--space-3);
    border-radius: var(--radius-pill);
    background: color-mix(in srgb, var(--c) 12%, var(--surface));
    border: 1px solid color-mix(in srgb, var(--c) 30%, var(--border));
    color: var(--text); font-weight: var(--weight-semibold); font-size: var(--text-base);
    text-decoration: none;
  }
  .pill.paused { background: var(--pause-soft); border-color: color-mix(in srgb, var(--pause) 40%, transparent); }
  .mini-collapsed { display: flex; flex-direction: column; align-items: center; gap: var(--space-1); padding: var(--space-2) 0; border-radius: var(--radius-sm); background: var(--brand-soft); color: var(--text); text-decoration: none; }
  .mini-collapsed.paused { background: var(--pause-soft); }
</style>
